import { Ball, SimulationConfig, EscapeRing, ShapeType } from '../types';
import { WebGLContext } from './webgl/WebGLContext';
import { BallRenderer } from './webgl/BallRenderer';
import { RingRenderer } from './webgl/RingRenderer';
import { BlastRenderer } from './webgl/BlastRenderer';
import { FXRenderer } from './webgl/FXRenderer';
import { TrailRenderer } from './webgl/TrailRenderer';
import { LineRenderer } from './webgl/LineRenderer';
import { WebGLUtils } from './webgl/WebGLUtils';

export class WebGLRenderer {
  private context: WebGLContext;
  private ballRenderer: BallRenderer;
  private ringRenderer: RingRenderer;
  private blastRenderer: BlastRenderer;
  private fxRenderer: FXRenderer;
  private trailRenderer: TrailRenderer;
  private lineRenderer: LineRenderer;

  constructor(canvas: HTMLCanvasElement) {
    this.context = new WebGLContext(canvas);
    this.ballRenderer = new BallRenderer(this.context);
    this.ringRenderer = new RingRenderer(this.context);
    this.blastRenderer = new BlastRenderer(this.context);
    this.fxRenderer = new FXRenderer(this.context);
    this.trailRenderer = new TrailRenderer(this.context);
    this.lineRenderer = new LineRenderer(this.context);
  }

  resize(width: number, height: number) {
    this.context.resize(width, height);
  }

  clear() {
    this.context.clear();
  }

  render(balls: Ball[], config: SimulationConfig, camera: { x: number, y: number, scale: number }, zoomLevel: number = 1.0) {
    this.ballRenderer.render(balls, config, camera, zoomLevel);
  }

  renderBallPreviews(previews: { pos: {x:number, y:number}, vel: {x:number, y:number}, radius: number, color: string }[], config: SimulationConfig, camera: { x: number, y: number, scale: number }, zoomLevel: number = 1.0) {
    const balls: Ball[] = previews.map((p, i) => ({
      id: `preview-${i}`,
      pos: p.pos,
      vel: p.vel,
      radius: p.radius,
      color: p.color,
      mass: 1,
      angle: Math.atan2(p.vel.y, p.vel.x),
      style: config.ballStyle
    }));

    // Render ghost balls
    this.ballRenderer.render(balls, { ...config, ballFillOpacity: 0.3, ballStrokeOpacity: 0.3 }, camera, zoomLevel);

    // Render direction arrows
    const arrowColor = WebGLUtils.hexToRgb(config.darkMode ? '#ffffff' : '#000000');
    balls.forEach(b => {
      const angle = Math.atan2(b.vel.y, b.vel.x);
      const length = 40; // Arrow length
      this.lineRenderer.renderArrow(b.pos, angle, length, arrowColor, 0.6, camera, zoomLevel);
    });
  }

  renderBlasts(blasts: any[], config: SimulationConfig, camera: { x: number, y: number, scale: number }, zoomLevel: number = 1.0) {
    this.blastRenderer.render(blasts, config, camera, zoomLevel);
  }

  renderFX(events: any[], config: SimulationConfig, camera: { x: number, y: number, scale: number }, zoomLevel: number = 1.0) {
    this.fxRenderer.render(events, config, camera, zoomLevel);
  }
  
  renderTrails(trailCircles: any[], config: SimulationConfig, camera: { x: number, y: number, scale: number }, zoomLevel: number = 1.0) {
    this.trailRenderer.render(trailCircles, config, camera, zoomLevel);
  }

  renderRings(rings: EscapeRing[], config: SimulationConfig, camera: { x: number, y: number, scale: number }, morph: { from: ShapeType, to: ShapeType, factor: number }, globalHue: number, zoomLevel: number = 1.0) {
    this.ringRenderer.render(rings, config, camera, morph, globalHue, zoomLevel);
  }
  
  renderContainer(config: SimulationConfig, camera: { x: number, y: number, scale: number }, morph: { from: ShapeType, to: ShapeType, factor: number }, radius: number, hue: number, zoomLevel: number = 1.0) {
    this.ringRenderer.renderContainer(config, camera, morph, radius, hue, zoomLevel);
  }
}
